import React, { useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import Header from '../components/Header';
import ForgotPasswordModal from '../components/ForgotPasswordModal';
import GoogleSignInButton from '../components/GoogleSignInButton';
import { isAdminSite } from '../utils/siteHosts';
import { useTranslations } from '../translations';
import { ASSETS } from '../constants/assets';
import { ApiError, login, resendVerificationEmail } from '../services/auth';
import { PASSWORD_MAX_LENGTH } from '../utils/passwordValidation';
import { appLinkClick, createPageUrl } from '../utils/appRoutes';
import './Login.css';

interface LoginProps {
  onNavigateToRegister: () => void;
  onNavigateToHome: () => void;
  onLogin: () => void;
  onNavigateToTerms?: () => void;
  onNavigateToPrivacy?: () => void;
}

const Login: React.FC<LoginProps> = ({
  onNavigateToRegister,
  onNavigateToHome,
  onLogin,
  onNavigateToTerms,
  onNavigateToPrivacy,
}) => {
  const t = useTranslations();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<Set<string>>(new Set());
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [emailVerificationPending, setEmailVerificationPending] = useState(false);
  const [resendStatus, setResendStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError(null);
    setEmailVerificationPending(false);
    setResendStatus('idle');
    if (value.trim()) {
      setValidationErrors(prev => {
        const newErrors = new Set(prev);
        newErrors.delete(name);
        return newErrors;
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const errors = new Set<string>();
    if (!formData.email.trim()) errors.add('email');
    if (!formData.password.trim()) errors.add('password');

    if (errors.size > 0) {
      setValidationErrors(errors);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      await login({
        email: formData.email,
        password: formData.password
      });

      onLogin();
    } catch (err) {
      if (err instanceof ApiError && (err.status === 429 || err.hasErrorCode('authentication_rate_limited'))) {
        setError(t.login.rateLimited);
      } else if (err instanceof ApiError && err.hasErrorCode('user_email_not_verified')) {
        setEmailVerificationPending(true);
        setError(t.login.emailNotVerified);
      } else if (err instanceof ApiError && err.hasErrorCode('user_password_login_unavailable')) {
        // The account has no password at all, so "wrong credentials" would send the user
        // in circles. Point them at Google or at setting a password.
        setError(t.googleSignIn.passwordLoginUnavailable);
      } else {
        setError(t.login.loginError);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (resendStatus === 'sending') {
      return;
    }
    setResendStatus('sending');
    try {
      await resendVerificationEmail(formData.email);
      setResendStatus('sent');
    } catch {
      setResendStatus('error');
    }
  };

  return (
    <div className="login-page">
      <Header
        isLoggedIn={false}
        onNavigateToLogin={() => {}}
        onNavigateToRegister={onNavigateToRegister}
        onNavigateToHome={onNavigateToHome}
      />
      <div className="login-content">
        <div className="login-form-container">
          <div className="login-form-wrapper">
            <h1 className="login-title">{t.login.title}</h1>
            
            <form className="login-form" onSubmit={handleSubmit}>
              <div className="form-field form-field-full">
                <label className="form-label">
                  {t.login.email}
                  <span className="required-asterisk">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  className={`form-input ${validationErrors.has('email') ? 'form-input-error' : ''}`}
                  value={formData.email}
                  onChange={handleInputChange}
                />
              </div>

              <div className="form-field form-field-full">
                <div className="password-label-row">
                  <label className="form-label">
                    {t.login.password}
                    <span className="required-asterisk">*</span>
                  </label>
                  <button type="button" className="forgot-password-link" onClick={() => setShowForgotPassword(true)}>
                    {t.login.forgotPassword}
                  </button>
                </div>
                <div className="password-input-wrapper">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    className={`form-input password-input ${validationErrors.has('password') ? 'form-input-error' : ''}`}
                    value={formData.password}
                    onChange={handleInputChange}
                    maxLength={PASSWORD_MAX_LENGTH}
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? t.login.hidePassword : t.login.showPassword}
                  >
                    {showPassword
                      ? <EyeOff className="password-toggle-icon" aria-hidden="true" />
                      : <Eye className="password-toggle-icon" aria-hidden="true" />}
                  </button>
                </div>
              </div>

              {error && (
                <div className="error-message">
                  {error}
                </div>
              )}
              {emailVerificationPending && (
                <div className="login-verification-actions">
                  <button
                    type="button"
                    className="login-resend-verification"
                    onClick={handleResendVerification}
                    disabled={resendStatus === 'sending'}
                  >
                    {resendStatus === 'sending'
                      ? t.login.resendingVerification
                      : t.login.resendVerification}
                  </button>
                  {resendStatus === 'sent' && (
                    <span className="login-verification-feedback">{t.login.verificationResent}</span>
                  )}
                  {resendStatus === 'error' && (
                    <span className="login-verification-error">{t.login.verificationResendError}</span>
                  )}
                </div>
              )}

              <button type="submit" className="btn-submit" disabled={isLoading}>
                {isLoading ? 'Se încarcă...' : t.login.submitButton}
              </button>
            </form>

            {/* Admins sign in with email and password; the admin site is not a Google OAuth origin. */}
            {!isAdminSite() && (
              <GoogleSignInButton
                onSuccess={onLogin}
                onNavigateToTerms={onNavigateToTerms}
                onNavigateToPrivacy={onNavigateToPrivacy}
              />
            )}

            <div className="register-section">
              <p className="register-text">{t.login.noAccount}</p>
              <button type="button" className="btn-register" onClick={onNavigateToRegister}>
                {t.login.createAccount}
              </button>
            </div>

            <p className="login-legal-links">
              <a href={createPageUrl('terms')} onClick={onNavigateToTerms ? appLinkClick(onNavigateToTerms) : undefined}>
                {t.footer.termsAndConditions}
              </a>
              {' · '}
              <a href={createPageUrl('privacy')} onClick={onNavigateToPrivacy ? appLinkClick(onNavigateToPrivacy) : undefined}>
                {t.footer.privacyPolicy}
              </a>
            </p>
          </div>
        </div>
        
        <div className="login-image-container">
          <img
            src={ASSETS.LOGIN_IMAGE}
            alt=""
            className="login-image"
          />
        </div>
      </div>

      {showForgotPassword && (
        <ForgotPasswordModal onClose={() => setShowForgotPassword(false)} />
      )}
    </div>
  );
};

export default Login;
